import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, List, ListItem, ListItemText, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/StarOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorderOutlined';
import { useAuth } from '../../context/AuthContext';
import { uploadAssetToR2, type AssetUploadKind } from '../../services/r2AssetUpload';
import { addBrandAsset, removeBrandAsset, updateBrandKit, type BrandAsset, type BrandKit } from '../../services/editingApi';
import { debugLog } from '../../utils/debugLog';
import { formatMs } from './editStudioModel';

interface BrandKitDialogProps {
  open: boolean;
  kit: BrandKit | null;
  onClose: () => void;
  onKit: (kit: BrandKit) => void;
}

const KIND_LABELS: Record<BrandAsset['kind'], string> = { image: 'Pictures', audio: 'Music and recordings', captions: 'Caption files' };
const ACCEPT: Record<BrandAsset['kind'], string> = { image: 'image/png,image/jpeg,image/webp,image/gif', audio: 'audio/*,.m4a,.mp3,.wav,.aac', captions: '.vtt,.srt,text/vtt' };
const UPLOAD_KIND: Record<BrandAsset['kind'], AssetUploadKind> = { image: 'image', audio: 'audio', captions: 'document' };
const MAX_BYTES: Record<BrandAsset['kind'], number> = { image: 10 * 1024 * 1024, audio: 200 * 1024 * 1024, captions: 2 * 1024 * 1024 };

/** The size of a picture or the length of a sound, read in the browser before upload. */
async function measure(file: File, kind: BrandAsset['kind']): Promise<{ width?: number; height?: number; durationMs?: number }> {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return {};
  const url = URL.createObjectURL(file);
  try {
    if (kind === 'image') {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve({});
        img.src = url;
      });
    }
    if (kind === 'audio') {
      return await new Promise((resolve) => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve({ durationMs: Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : undefined });
        audio.onerror = () => resolve({});
        audio.src = url;
      });
    }
    return {};
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The account's logo, pictures, music, recordings and caption files. */
const BrandKitDialog: React.FC<BrandKitDialogProps> = ({ open, kit, onClose, onKit }) => {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [name, setName] = useState(kit?.displayName || '');
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  React.useEffect(() => { setName(kit?.displayName || ''); }, [kit?.displayName]);

  const upload = async (kind: BrandAsset['kind'], file: File) => {
    setProblem(null);
    if (file.size > MAX_BYTES[kind]) {
      setProblem(`That file is too big. ${KIND_LABELS[kind]} go up to ${Math.round(MAX_BYTES[kind] / 1024 / 1024)} MB.`);
      return;
    }
    setBusy(`Uploading ${file.name}…`);
    setProgress(0);
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in again to upload.');
      const contentType = file.type || (kind === 'captions' ? 'text/vtt' : 'application/octet-stream');
      const stored = await uploadAssetToR2(file, UPLOAD_KIND[kind], token, (percent) => setProgress(percent));
      const measured = await measure(file, kind);
      const updated = await addBrandAsset({ kind, name: file.name.replace(/\.[^.]+$/, '').slice(0, 80) || file.name, objectKey: stored.objectKey, url: stored.url, contentType: stored.contentType || contentType, sizeBytes: stored.sizeBytes || file.size, ...measured });
      onKit(updated);
    } catch (error) {
      debugLog('Brand asset upload failed', error);
      setProblem(error instanceof Error ? error.message : 'The upload didn’t work. Try again.');
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const remove = async (asset: BrandAsset) => {
    setProblem(null);
    setBusy(`Removing ${asset.name}…`);
    try {
      onKit(await removeBrandAsset(asset.id));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'It couldn’t be removed.');
    } finally {
      setBusy(null);
    }
  };

  const setLogo = async (asset: BrandAsset | null) => {
    setProblem(null);
    try {
      onKit(await updateBrandKit({ logoAssetId: asset ? asset.id : null }));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That didn’t save.');
    }
  };

  const saveName = async () => {
    if ((kit?.displayName || '') === name.trim()) return;
    try {
      onKit(await updateBrandKit({ displayName: name.trim() || null }));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That didn’t save.');
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Brand kit</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your logo, pictures, music, recordings and caption files, ready for every quick edit. Only you can see them.
        </Typography>
        {problem ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProblem(null)}>{problem}</Alert> : null}
        {busy ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">{busy}</Typography>
            <LinearProgress variant={progress === null ? 'indeterminate' : 'determinate'} value={progress ?? 0} />
          </Box>
        ) : null}
        <TextField size="small" fullWidth label="Your name, as a lower third shows it" value={name} onChange={(event) => setName(event.target.value)} onBlur={saveName} inputProps={{ maxLength: 120, 'data-testid': 'brand-name' }} sx={{ mb: 2 }} />
        {(['image', 'audio', 'captions'] as BrandAsset['kind'][]).map((kind) => {
          const items = (kit?.assets || []).filter((asset) => asset.kind === kind);
          return (
            <Box key={kind} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">{KIND_LABELS[kind]}</Typography>
                <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => inputs.current[kind]?.click()} data-testid={`upload-${kind}`}>Upload</Button>
                <input
                  ref={(node) => { inputs.current[kind] = node; }}
                  type="file"
                  accept={ACCEPT[kind]}
                  hidden
                  data-testid={`upload-input-${kind}`}
                  onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) upload(kind, file); }}
                />
              </Stack>
              {items.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {kind === 'image' ? 'A PNG with transparency makes the best logo.' : kind === 'audio' ? 'Music you have the rights to, and recorded voiceovers.' : 'VTT or SRT files, if you have captions already.'}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {items.map((asset) => (
                    <ListItem
                      key={asset.id}
                      disableGutters
                      secondaryAction={(
                        <Stack direction="row">
                          {kind === 'image' ? (
                            <IconButton size="small" aria-label={kit?.logoAssetId === asset.id ? `${asset.name} is the logo` : `Use ${asset.name} as the logo`} onClick={() => setLogo(kit?.logoAssetId === asset.id ? null : asset)}>
                              {kit?.logoAssetId === asset.id ? <StarIcon fontSize="inherit" color="primary" /> : <StarBorderIcon fontSize="inherit" />}
                            </IconButton>
                          ) : null}
                          <IconButton size="small" aria-label={`Remove ${asset.name}`} disabled={Boolean(busy)} onClick={() => remove(asset)}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                        </Stack>
                      )}
                    >
                      {kind === 'image' ? <img src={asset.url} alt="" style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 12, background: '#eee', borderRadius: 4 }} /> : null}
                      <ListItemText primary={asset.name} secondary={[asset.width && asset.height ? `${asset.width}×${asset.height}` : null, asset.durationMs ? formatMs(asset.durationMs) : null, `${Math.max(1, Math.round(asset.sizeBytes / 1024))} KB`].filter(Boolean).join(' · ')} primaryTypographyProps={{ variant: 'body2', noWrap: true }} secondaryTypographyProps={{ variant: 'caption' }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(busy)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandKitDialog;
